import type { ReactElement, ReactNode } from 'react';
import { DialogEditor, RenderCellProps, RenderEditCellProps, SelectEditor } from 'components/DataGrid';
import { createColumn } from './createColumn';
import type { GridRow, GridSummaryRow } from '../types';
import { useOOHCategoryFormatOption, useOOHCategoryFormatOptions } from 'modules/ooh/useOOHCategoryFormatOptions';
import { formatOptionLabel } from 'modules/ooh/OOHCategoryFormatField';
import { wrapContainer } from './style.css';
import { usePlanController } from '../../usePlanController';
import { OOHFormatForm } from 'modules/ooh/OOHFormatForm';
import { initialFormatValues } from 'modules/ooh';
import { TitleContainer } from 'components/TitleContainer';

function RenderCategoryFormat(props: RenderCellProps<GridRow, GridSummaryRow>): ReactNode {
  const value = useOOHCategoryFormatOption(props.row.ooh_category_format);
  if (!value) {
    return null;
  }
  return formatOptionLabel(value);
}

function Edit(props: RenderEditCellProps<GridRow, any>): ReactElement {
  const options = useOOHCategoryFormatOptions();
  return <SelectEditor options={options} {...props} isSimple isSearchable formatOptionLabel={formatOptionLabel} />;
}

export const OOHCategoryFormatColumn = createColumn(
  {
    key: 'ooh_category_format',
    name: 'Category',
    width: 160,
  },
  {
    renderView: (props) => (props.row.kind === 'MEDIA' ? <RenderCategoryFormat {...props} /> : null),
    renderEdit: (props) => <Edit {...props} />,
    displayCellContent: true,
  },
);

function EditFormat(props: RenderEditCellProps<GridRow, any>): ReactNode {
  const controller = usePlanController();
  return (
    <DialogEditor
      {...props}
      submitHandler={async (values) =>
        await controller.formRequest({
          method: 'PUT',
          url: `plan/rows/${props.row.id}/ooh_format/`,
          body: values,
        })
      }
      dialogHeader="Format"
      testId="ooh-format-edit"
      initialValues={props.row.ooh_format || initialFormatValues}
    >
      <OOHFormatForm />
    </DialogEditor>
  );
}

function renderFormatValue(props: RenderCellProps<GridRow, GridSummaryRow>): ReactNode {
  const value = props.row.ooh_format;
  // dash for locked
  if (!value) {
    return props.isCellEditable ? null : '—';
  }
  const subtitle = [value.dimensions, value.area].filter(Boolean).join(' / ') || null;
  return (
    <span className={wrapContainer} title={value.display_type}>
      <TitleContainer title={value.display_type} subtitle={subtitle} />
    </span>
  );
}

export const OOHFormatColumn = createColumn(
  {
    key: 'ooh_format',
    name: 'Format',
    width: 140,
  },
  {
    renderView: (props) => (props.row.kind === 'MEDIA' ? renderFormatValue(props) : null),
    renderEdit: (props) => <EditFormat {...props} />,
    displayCellContent: true,
  },
);
